import debouce from 'lodash.debounce';
import moment from 'moment';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Calendar } from 'react-date-range';
import { Mobile } from '../../../App';

import { SidebarModal } from '../../../components/common/Sidebar/Sidebar';
import {
  Button,
  Flexed,
  PageTitle,
  SectionSeparator,
  TabSelectButton
} from '../../../styles/GlobalComponents';
import {
  FlexBetween,
  FlexCenter,
  FlexJustifyCenter
} from '../../../styles/stylesStyled';
import { EarningDetails } from './EarningDetails';
import { EarningsHistoryItem } from './EarningsHistoryItem';
import {
  ClearAllFilters,
  Collapsable,
  CollapsableAbsolute,
  DateInputContainer,
  DateInputLabelContainer,
  FilterButton,
  FilterContainer,
  SearchInput,
  SortContainer
} from './EarningsHistoryStyled';

import { useTranslation } from 'react-i18next';
import { useWindowWide } from '../../../helpers/wideScreen';
import { P1, P2, P3 } from '../../../styles/FontStyles';
import { color } from '../../../styles/GlobalStyles';
import {
  Icon,
  IconCalendar,
  IconFilter,
  IconSort,
  IconStructureNavArrow,
  IconX
} from '../../../styles/Icons';

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { useAppSelector } from '../../../app/hooks';
import { AnimatedItem } from '../../../components/animation/AnimatedItem';
import {
  DateToggleEnum,
  EarningsHistoryFilterEnum,
  EarningsPageEnum,
  EarningsSortByEnum,
  FilterToggleEnum
} from '../Earnings.types';

interface EarningsHistoryProps {
  countPerPage: number;
  setCountPerPage: Dispatch<SetStateAction<number>>;
  setMobilePage: Dispatch<SetStateAction<EarningsPageEnum>>;
  filterToggle: FilterToggleEnum | null;
  setFilterToggle: Dispatch<SetStateAction<FilterToggleEnum | null>>;
  setEarningsType: Dispatch<SetStateAction<number[] | null>>;
  filter: EarningsHistoryFilterEnum | null;
  setFilter: Dispatch<SetStateAction<EarningsHistoryFilterEnum | null>>;
  HandleChangeFilter: (filter: EarningsHistoryFilterEnum | null) => void;
  sort: EarningsSortByEnum | null;
  setSort: Dispatch<SetStateAction<EarningsSortByEnum | null>>;
  startDate: Date | null;
  setStartDate: Dispatch<SetStateAction<Date | null>>;
  endDate: Date | null;
  setEndDate: Dispatch<SetStateAction<Date | null>>;
  setSearchId: Dispatch<SetStateAction<number | string>>;
}

export const EarningsHistory = ({
  countPerPage,
  setCountPerPage,
  setMobilePage,
  filterToggle,
  setFilterToggle,
  setEarningsType,
  filter,
  setFilter,
  HandleChangeFilter,
  sort,
  setSort,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  setSearchId
}: EarningsHistoryProps) => {
  const { t } = useTranslation('earnings');

  const { history } = useAppSelector((state) => state.earnings);
  const [dateToggle, setDateToggle] = useState<DateToggleEnum | null>(null);
  const [isEarningsDetailsOpened, setIsEarningsDetailsOpened] = useState(false);

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setSearchId(e.target.value);
  };

  const wide = useWindowWide(800);

  const debouncedResults = useMemo(() => {
    return debouce(handleChange, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  const SortSelected = () => {
    const BtnX = () => (
      <Icon
        color={color.colors.neutral50}
        onClick={() => setSort(null)}
        ml='10'>
        <IconX size='15' />
      </Icon>
    );

    const ArrowSort = ({ down }: { down?: boolean }) => (
      <Icon
        color={color.colors.neutral50}
        ml='2'
        style={{
          transform: down ? 'rotate(180deg)' : ''
        }}>
        <IconStructureNavArrow size='15' />
      </Icon>
    );

    switch (sort) {
      case EarningsSortByEnum.DATE_UP:
        return (
          <div>
            {t('date')}
            <ArrowSort />
            <BtnX />
          </div>
        );
      case EarningsSortByEnum.DATE_DOWN:
        return (
          <div>
            {t('date')}
            <ArrowSort down />
            <BtnX />
          </div>
        );
      case EarningsSortByEnum.PRICE_UP:
        return (
          <div>
            {t('price')}
            <ArrowSort />
            <BtnX />
          </div>
        );
      case EarningsSortByEnum.PRICE_DOWN:
        return (
          <div>
            {t('price')}
            <ArrowSort down />
            <BtnX />
          </div>
        );
      default:
        return null;
    }
  };

  const HandleToggleFilter = (toggle: FilterToggleEnum) => {
    setFilterToggle(filterToggle === toggle ? null : toggle);
    setDateToggle(null);
  };

  const HandleIsFilterToggleSelected = (toggle: FilterToggleEnum) =>
    filterToggle === toggle;

  const HandleToggleDate = (toggle: DateToggleEnum) => {
    setDateToggle(dateToggle === toggle ? null : toggle);
  };

  const HandleIsDateToggleSelected = (toggle: DateToggleEnum) =>
    dateToggle === toggle;

  const HandleChangeDateStart = (date: Date) => {
    setStartDate(date);
    setDateToggle(null);
  };

  const HandleChangeDateEnd = (date: Date) => {
    setEndDate(date);
    setDateToggle(null);
  };

  const HandleChangeSort = (sort: EarningsSortByEnum) => {
    setSort(sort);
    setFilterToggle(null);
  };

  const HandleClearAllFilters = () => {
    setFilter(null);
    setSort(null);
    setStartDate(null);
    setEndDate(null);
    setFilterToggle(null);
    setDateToggle(null);
    setEarningsType(null);
  };

  const IsAnyFilterSelected = filter || startDate || endDate || sort;

  const showMoreBonuses = (countPerPage: number) => {
    let page = countPerPage;
    page = page + 50;

    if (page <= 100) {
      setCountPerPage(page);
    }
  };

  const HandleIsFilterSelected = (toggle: EarningsHistoryFilterEnum) =>
    filter === toggle;

  const filterToggleButtons = [
    {
      value: FilterToggleEnum.FILTER
    },
    {
      value: FilterToggleEnum.SORT
    },
    {
      value: FilterToggleEnum.DATE
    }
  ];

  const RenderToggleFilterIcons = (value: FilterToggleEnum) => {
    switch (value) {
      case FilterToggleEnum.FILTER:
        return <IconFilter />;
      case FilterToggleEnum.SORT:
        return <IconSort />;
      case FilterToggleEnum.DATE:
        return <IconCalendar />;

      default:
        break;
    }
  };

  const tabSelectButtons = [
    {
      name: t('direct_bonus'),
      value: EarningsHistoryFilterEnum.DIRECT_BONUS
    },
    {
      name: t('team_bonus'),
      value: EarningsHistoryFilterEnum.TEAM_BONUS
    },

    {
      name: t('matching_bonus'),
      value: EarningsHistoryFilterEnum.MATCHING_BONUS
    },
    {
      name: t('cashback_bonus'),
      value: EarningsHistoryFilterEnum.CASHBACK_BONUS
    },

    {
      name: t('package_rank'),
      value: EarningsHistoryFilterEnum.PACKAGE_RANK
    },

    {
      name: 'Paid',
      value: EarningsHistoryFilterEnum.PAID
    },
    {
      name: 'Pending',
      value: EarningsHistoryFilterEnum.PENDING
    },

    {
      name: 'My Life Points',
      value: EarningsHistoryFilterEnum.MYLIFE
    }
  ];

  const sortToggleButtons = [
    {
      comp: <IconFilter />,
      value: EarningsSortByEnum.DATE_UP,
      name: t('latest_date')
    },
    {
      comp: <IconSort />,
      value: EarningsSortByEnum.DATE_DOWN,
      name: t('oldest_date')
    },
    {
      comp: <IconCalendar />,
      value: EarningsSortByEnum.PRICE_UP,
      name: t('price_low_to_high')
    },
    {
      comp: <IconCalendar />,
      value: EarningsSortByEnum.PRICE_DOWN,
      name: t('price_high_to_low')
    }
  ];

  const sortToggleButtonsIcons = (value: EarningsSortByEnum) => {
    switch (value) {
      case EarningsSortByEnum.DATE_UP:
        return (
          <Icon color={color.colors.neutral900} ml='5'>
            <IconStructureNavArrow size='18' />
          </Icon>
        );
      case EarningsSortByEnum.DATE_DOWN:
        return (
          <Icon
            color={color.colors.neutral900}
            ml='5'
            style={{ transform: 'rotate(180deg)' }}>
            <IconStructureNavArrow size='18' />
          </Icon>
        );
      case EarningsSortByEnum.PRICE_UP:
        return (
          <Icon color={color.colors.neutral900} ml='5'>
            <IconStructureNavArrow size='18' />
          </Icon>
        );
      case EarningsSortByEnum.PRICE_DOWN:
        return (
          <Icon
            color={color.colors.neutral900}
            ml='5'
            style={{ transform: 'rotate(180deg)' }}>
            <IconStructureNavArrow size='18' />
          </Icon>
        );

      default:
        break;
    }
  };

  return (
    <div>
      <FlexBetween style={{ marginBottom: 20 }}>
        <PageTitle>{t('title_history')}</PageTitle>
        <Mobile>
          <P1
            pointer
            color={color.colors.primary900}
            onClick={() => setMobilePage(EarningsPageEnum.EARNINGS_INFO)}>
            {t('show_earnings')}
          </P1>
        </Mobile>
      </FlexBetween>
      <FilterContainer>
        <SearchInput
          placeholder={t('search_label')}
          onChange={debouncedResults}
        />

        {filterToggleButtons.map(({ value }, i) => (
          <FilterButton key={i} onClick={() => HandleToggleFilter(value)}>
            <Icon color={color.colors.neutral50}>
              {HandleIsFilterToggleSelected(value) ? (
                <IconX />
              ) : (
                RenderToggleFilterIcons(value)
              )}
            </Icon>
          </FilterButton>
        ))}
      </FilterContainer>

      <Collapsable
        expanded={HandleIsFilterToggleSelected(FilterToggleEnum.FILTER)}
        height={
          HandleIsFilterToggleSelected(FilterToggleEnum.FILTER)
            ? '200px'
            : '100px'
        }>
        <Flexed wrap='true'>
          {tabSelectButtons.map(({ name, value }, i) => (
            <TabSelectButton
              key={i}
              m='0 10px 15px 0'
              selected={HandleIsFilterSelected(value)}
              onClick={() => HandleChangeFilter(value)}>
              <div>{name}</div>
            </TabSelectButton>
          ))}
        </Flexed>
      </Collapsable>
      <Collapsable
        expanded={HandleIsFilterToggleSelected(FilterToggleEnum.SORT)}
        height={'256px'}>
        <SortContainer>
          {sortToggleButtons.map(({ value, name }, i) => (
            <div key={value}>
              <div onClick={() => HandleChangeSort(value)}>
                <P2>{name}</P2>
                {sortToggleButtonsIcons(value)}
              </div>
              {i + 1 !== sortToggleButtons.length}
              <SectionSeparator margin='0' />
            </div>
          ))}
        </SortContainer>
      </Collapsable>
      <Collapsable
        expanded={HandleIsFilterToggleSelected(FilterToggleEnum.DATE)}
        height={'100px'}>
        <FlexBetween>
          <DateInputContainer>
            <DateInputLabelContainer
              onClick={() => HandleToggleDate(DateToggleEnum.START)}>
              <Icon color={color.colors.neutral900} mr='16'>
                <IconCalendar />
              </Icon>
              {startDate
                ? moment(startDate).format('DD.MM.YYYY')
                : t('start_date')}
            </DateInputLabelContainer>
            <CollapsableAbsolute
              expanded={HandleIsDateToggleSelected(DateToggleEnum.START)}>
              <Calendar
                onChange={(item) => {
                  HandleChangeDateStart(item);
                }}
                date={new Date()}
              />
            </CollapsableAbsolute>
          </DateInputContainer>
          <DateInputContainer>
            <DateInputLabelContainer
              onClick={() => HandleToggleDate(DateToggleEnum.END)}>
              <Icon color={color.colors.neutral900} mr='16'>
                <IconCalendar />
              </Icon>
              {endDate ? moment(endDate).format('DD.MM.YYYY') : t('end_date')}
            </DateInputLabelContainer>
            <CollapsableAbsolute
              expanded={HandleIsDateToggleSelected(DateToggleEnum.END)}
              right>
              <Calendar
                onChange={(item) => {
                  HandleChangeDateEnd(item);
                }}
                date={new Date()}
                minDate={startDate || undefined}
              />
            </CollapsableAbsolute>
          </DateInputContainer>
        </FlexBetween>
      </Collapsable>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: wide ? 'calc(100vh - 255px)' : ''
        }}>
        <FlexCenter
          style={{
            visibility: IsAnyFilterSelected ? 'visible' : 'hidden',
            flexWrap: 'wrap'
          }}>
          {tabSelectButtons
            .filter(({ value }) => value === filter)
            .map(({ name }, i) => (
              <TabSelectButton key={i} light selected={true} m='0 10px 15px 0'>
                <div>
                  {name}{' '}
                  <Icon
                    color={color.colors.neutral50}
                    onClick={() => {
                      HandleChangeFilter(null);
                    }}
                    ml='10'>
                    <IconX size='15' />
                  </Icon>
                </div>
              </TabSelectButton>
            ))}

          {sort && (
            <TabSelectButton light selected={true} m='0 10px 15px 0'>
              {SortSelected()}
            </TabSelectButton>
          )}

          {startDate && endDate && (
            <TabSelectButton light selected={true} m='0 10px 15px 0'>
              <div>
                {moment(startDate).format('DD.MM.YYYY') +
                  ' - ' +
                  moment(endDate).format('DD.MM.YYYY')}

                <Icon
                  color={color.colors.neutral50}
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                  }}
                  ml='10'>
                  <IconX size='15' />
                </Icon>
              </div>
            </TabSelectButton>
          )}

          <ClearAllFilters
            show={IsAnyFilterSelected}
            onClick={() => HandleClearAllFilters()}>
            {t('clear_all')}
          </ClearAllFilters>
        </FlexCenter>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflowY: wide ? 'auto' : 'visible',
            marginTop: 30,
            margin: '30px -10px 0 -10px',
            padding: '0 10px',
            paddingBottom: 10
          }}>
          {history?.map((earning: any, i: number) => (
            <AnimatedItem index={i} key={i}>
              <EarningsHistoryItem
                key={i}
                earning={earning}
                setIsEarningsDetailsOpened={setIsEarningsDetailsOpened}
              />
            </AnimatedItem>
          ))}
          {history?.length > 49 && countPerPage < 100 && (
            <FlexJustifyCenter>
              <Button
                w='200px'
                mt='10'
                mb='20'
                onClick={() => showMoreBonuses(countPerPage)}>
                Show more
              </Button>
            </FlexJustifyCenter>
          )}
          {history?.length === 0 && (
            <P3 color={color.colors.neutral500}>{t('no_earnigns')}</P3>
          )}
        </div>
      </div>
      <SidebarModal
        isOpen={isEarningsDetailsOpened}
        setOpen={setIsEarningsDetailsOpened}
        title={t('earning_details')}
        sidebarWidth={'511px'}>
        <EarningDetails
          setIsEarningsDetailsOpened={setIsEarningsDetailsOpened}
        />
      </SidebarModal>
    </div>
  );
};
